<template>
    <div class="container admin-table-page">
        <!-- ==================== TÍTULO ==================== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100">
                <div class="d-flex align-items-center">
                    <b-button class="page-back-btn mr-2" variant="link" @click="goBack">
                        <font-awesome-icon :icon="['fas', 'arrow-alt-circle-left']" class="back-icon" />
                    </b-button>
                    <h2 class="param-title">{{ tituloConsolidado || labels.title }}</h2>
                </div>
            </div>
        </div>

        <!-- ==================== FILTROS ==================== -->
        <div class="param-title-wrap niif-header">
            <div class="d-flex align-items-center w-100" style="flex-wrap: wrap; gap: 8px;">

                <!-- Filtros principales -->
                <div class="filters-container d-flex align-items-center" style="flex-wrap: wrap; gap: 8px;">

                    <!-- Reporte (solo para reclasificación) -->
                    <b-form-select
                        v-if="reclasifica"
                        v-model="dialogReportes"
                        @change="onFiltroChange"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectReport }}
                        </b-form-select-option>
                        <option
                            v-for="reporte in reportes"
                            :key="reporte.id"
                            :value="reporte.codigo"
                        >
                            {{ reporte?.nombre }}
                        </option>
                    </b-form-select>

                    <!-- Vigencia -->
                    <b-form-select
                        v-model="dialogFechas"
                        @change="onFiltroChange"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectValidity }}
                        </b-form-select-option>
                        <option
                            v-for="fecha in fechas"
                            :key="fecha.fecha"
                            :value="fecha.fecha"
                        >
                            {{ fecha?.fecha }}
                        </option>
                    </b-form-select>

                    <!-- Grupo -->
                    <b-form-select
                        v-model="grupoId"
                        @change="(value) => filtraCompaniasPorGrupo(value)"
                        class="filter-select"
                    >
                        <option value="all">
                            {{ labels.allGroups }}
                        </option>
                        <option
                            v-for="grupo in listGrupos"
                            :key="'grupo-' + grupo.id"
                            :value="grupo.id"
                        >
                            {{ grupo?.nombre }}
                        </option>
                    </b-form-select>

                    <!-- Compañía -->
                    <b-form-select
                        v-model="dialogCompanias"
                        @change="seleccionaCompania()"
                        class="filter-select"
                    >
                        <b-form-select-option value="">
                            {{ labels.selectCompany }}
                        </b-form-select-option>
                        <option
                            v-for="compania in companiasFiltradas"
                            :key="compania.id"
                            :value="compania.id"
                        >
                            {{ compania?.acronym ? compania?.acronym + ' ' : '' }}
                            {{ compania?.nombre }}
                        </option>
                    </b-form-select>

                </div>

                <!-- Botón Consultar -->
                <div class="filters-container d-flex align-items-center">
                    <b-button
                        class="consult-btn"
                        @click="consultar"
                        :disabled="
                            !dialogFechas ||
                            (reclasifica && !dialogReportes) ||
                            (!dialogCompanias && !grupoId)
                        "
                    >
                        <font-awesome-icon :icon="['fas', 'search']" class="mr-1" />
                        {{ labels.consultar }}
                    </b-button>
                </div>

            </div>
        </div>

        <!-- ========================================================= -->
        <!-- ==================== CONTENIDO PRINCIPAL ================= -->
        <!-- ========================================================= -->
        <div class="admin-table-block mt-4">

            <!-- ==================== TABLA PRINCIPAL ==================== -->
            <div v-if="!isLoading && !showDialogEdit">
                <CorporateDataTable
                    :items="ajustesTopside"
                    :fields="allFields"
                    :labels="tableLabels"
                    :loading="false"
                    :default-per-page="ajustesTopside.length || 10"
                    :page-options="[ajustesTopside.length || 10]"
                    :narrow="false"
                    :table-min-height="500"
                    :table-max-height="500"
                    :show-search="true"
                    :allow-column-toggle="true"
                    :enable-sorting="true"
                    :filter-defs="tableFilterDefs"
                    :show-pagination="false"
                    :show-rows-per-page="false"
                    @row-selected="onRowSelected"
                >
                    <template #toolbar-actions>
                        <div class="niif-toolbar-right">
                            <b-button
                                size="sm"
                                class="table-toolbar-add-btn"
                                @click="crear"
                                :disabled="!fgl_consultar || !dialogCompanias || isVigenciaBloqueada"
                            >
                                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                                {{ textoBotonNuevo() }}
                            </b-button>
                        </div>
                    </template>

                    <template #cell(secuencia)="data">
                        <div class="text-center"><strong>{{ data.item?.secuencia }}</strong></div>
                    </template>

                    <template #cell(compania)="data">
                        <div class="text-left">{{ data.item?.nombreCompania || obtenerNombreCompania(data.item?.idCompania) }}</div>
                    </template>

                    <template #cell(cuenta)="data">
                        <div class="text-left font-monospace">{{ data.item?.cuenta }}</div>
                    </template>

                    <template #cell(descripcion)="data">
                        <div class="text-left" :title="data.item?.descripcion">{{ data.item?.descripcion }}</div>
                    </template>

                    <template #cell(niif)="data">
                        <div class="text-left">{{ reclasifica ? f_niif(data.item?.niif) : f_niifCuenta(data.item?.cuenta) }}</div>
                    </template>

                    <template #cell(niif1)="data">
                        <div class="text-left" v-if="reclasifica">{{ f_niif(data.item?.niif1) }}</div>
                    </template>

                    <template #cell(ajuste_funcional1)="data">
                        <div class="text-right font-monospace">{{ data.item?.ajuste_funcional1 }}</div>
                    </template>

                    <template #cell(ajuste_promedio1)="data">
                        <div class="text-right font-monospace">{{ data.item?.ajuste_promedio1 }}</div>
                    </template>

                    <template #cell(ajuste_interna1)="data">
                        <div class="text-right font-monospace">{{ data.item?.ajuste_interna1 }}</div>
                    </template>

                    <template #cell(ajuste_funcional2)="data">
                        <div class="text-right font-monospace">{{ data.item?.ajuste_funcional2 }}</div>
                    </template>

                    <template #cell(ajuste_promedio2)="data">
                        <div class="text-right font-monospace">{{ data.item?.ajuste_promedio2 }}</div>
                    </template>

                    <template #cell(ajuste_interna2)="data">
                        <div class="text-right font-monospace">{{ data.item?.ajuste_interna2 }}</div>
                    </template>

                    <template #cell(accionEditar)="data">
                        <b-button
                            v-if="fgl_consultar && !reclasifica"
                            style="background-color: #FF924C; width: 30px; height: 30px; padding: 0; display: flex; align-items: center; justify-content: center; border: none; border-radius: 4px;"
                            class="table-action-btn"
                            @click.stop="f_muestra(data.item)"
                            :disabled="isVigenciaBloqueada"
                            :title="labels.edit"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" size="xs" style="color: white;" />
                        </b-button>
                    </template>

                    <template #empty-state>
                        <div class="text-center py-5">
                            <font-awesome-icon :icon="['fas', 'file-alt']" size="4x" class="text-muted mb-3" style="opacity: 0.3;" />
                            <p class="h4 text-muted">
                                {{ fgl_consultar ? labels.empty : labels.selectCriteria }}
                            </p>
                            <b-button
                                v-if="fgl_consultar && dialogCompanias"
                                @click="crear"
                                variant="primary"
                                class="mt-3"
                                :disabled="isVigenciaBloqueada"
                            >
                                <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
                                {{ textoBotonNuevo() }}
                            </b-button>
                        </div>
                    </template>
                </CorporateDataTable>
            </div>

            <!-- ==================== FORMULARIO CREAR TOPSIDE ==================== -->
            <b-container v-if="showDialogEdit" class="mt-4 mb-5 p-4 border rounded bg-light">
                <h4 class="text-center mb-4"><b><i>{{ f_titulo() }} - Nuevo Registro</i></b></h4>

                <!-- BÚSQUEDA DE CUENTA -->
                <b-row class="mb-4">
                    <b-col cols="12">
                        <label class="fw-bold mb-2"><b>{{ labels.searchAccount }}:</b></label>
                        <b-input-group>
                            <b-input-group-prepend is-text><font-awesome-icon :icon="['fas', 'search']" /></b-input-group-prepend>
                            <b-form-input v-model="busquedaCuenta" @input="buscarCuenta"
                                placeholder="Ingrese código o descripción de la cuenta..."
                                class="form-control-lg"></b-form-input>
                            <b-input-group-append>
                                <b-button variant="secondary" @click="limpiarBusqueda">{{ labels.clear }}</b-button>
                            </b-input-group-append>
                        </b-input-group>
                        <b-list-group v-if="sugerenciasCuentas?.length > 0" class="mt-2" style="max-height: 200px; overflow-y: auto;">
                            <b-list-group-item v-for="cuenta in sugerenciasCuentas" :key="cuenta.id"
                                @click="seleccionarCuenta(cuenta)" class="cursor-pointer hover-bg"
                                :active="v_id === cuenta.id">
                                <strong>{{ cuenta?.cuenta }}</strong> - {{ cuenta?.descripcion }}
                                <small class="text-muted d-block">NIIF: {{ f_niifCuenta1(cuenta?.cuenta) }} - {{ f_niif(f_niifCuenta1(cuenta?.cuenta)) }}</small>
                            </b-list-group-item>
                        </b-list-group>
                    </b-col>
                </b-row>

                <!-- CUENTA SELECCIONADA -->
                <b-row v-if="v_cuenta" class="mb-4 p-3 bg-white border rounded">
                    <b-col md="3" class="border-right">
                        <small><b>{{ labels.code }}:</b></small>
                        <h5 class="mb-0 text-primary"><strong>{{ v_cuenta }}</strong></h5>
                    </b-col>
                    <b-col md="5" class="border-right">
                        <small><b>{{ labels.descripcion }}:</b></small>
                        <h5 class="mb-0"><strong>{{ v_descripcion }}</strong></h5>
                    </b-col>
                    <b-col md="4">
                        <small><b>{{ labels.niif }}:</b></small>
                        <h5 class="mb-0"><strong>{{ v_niif }} - {{ f_niif(v_niif) }}</strong></h5>
                    </b-col>
                </b-row>

                <!-- AJUSTES -->
                <b-row class="mb-1">
                    <b-col cols="4" class="border bg-secondary text-white">
                        <h6 class="text-center mb-1"><b><i>{{ labels.funcional1 }}</i></b></h6>
                    </b-col>
                    <b-col cols="4" class="border bg-secondary text-white">
                        <h6 class="text-center mb-1"><b><i>{{ labels.promedio1 }}</i></b></h6>
                    </b-col>
                    <b-col cols="4" class="border bg-secondary text-white">
                        <h6 class="text-center mb-1"><b><i>{{ labels.interna1 }}</i></b></h6>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col cols="4" class="border p-1">
                        <b-form-input v-model="v_ajuste_funcional1" type="number" step="0.01"
                            class="text-right form-control-lg" :disabled="isVigenciaBloqueada"></b-form-input>
                    </b-col>
                    <b-col cols="4" class="border p-1">
                        <b-form-input v-model="v_ajuste_promedio1" type="number" step="0.01"
                            class="text-right form-control-lg" :disabled="isVigenciaBloqueada"></b-form-input>
                    </b-col>
                    <b-col cols="4" class="border p-1">
                        <b-form-input v-model="v_ajuste_interna1" type="number" step="0.01"
                            class="text-right form-control-lg" :disabled="isVigenciaBloqueada"></b-form-input>
                    </b-col>
                </b-row>

                <b-row class="mb-1">
                    <b-col cols="4" class="border bg-secondary text-white">
                        <h6 class="text-center mb-1"><b><i>{{ labels.funcional2 }}</i></b></h6>
                    </b-col>
                    <b-col cols="4" class="border bg-secondary text-white">
                        <h6 class="text-center mb-1"><b><i>{{ labels.promedio2 }}</i></b></h6>
                    </b-col>
                    <b-col cols="4" class="border bg-secondary text-white">
                        <h6 class="text-center mb-1"><b><i>{{ labels.interna2 }}</i></b></h6>
                    </b-col>
                </b-row>
                <b-row class="mb-3">
                    <b-col cols="4" class="border p-1">
                        <b-form-input v-model="v_ajuste_funcional2" type="number" step="0.01"
                            class="text-right form-control-lg" :disabled="isVigenciaBloqueada"></b-form-input>
                    </b-col>
                    <b-col cols="4" class="border p-1">
                        <b-form-input v-model="v_ajuste_promedio2" type="number" step="0.01"
                            class="text-right form-control-lg" :disabled="isVigenciaBloqueada"></b-form-input>
                    </b-col>
                    <b-col cols="4" class="border p-1">
                        <b-form-input v-model="v_ajuste_interna2" type="number" step="0.01"
                            class="text-right form-control-lg" :disabled="isVigenciaBloqueada"></b-form-input>
                    </b-col>
                </b-row>

                <!-- RECLASIFICACIÓN NIIF -->
                <b-row v-if="reclasifica" class="mb-4">
                    <b-col cols="6" class="border bg-light p-2">
                        <h6 class="text-center mb-0"><b><i>{{ labels.niif1 }}</i></b></h6>
                    </b-col>
                    <b-col cols="6" class="border p-1">
                        <b-form-select v-model="v_niif1" :options="opcionesNiifSelect"
                            class="form-control-lg" :disabled="isVigenciaBloqueada"></b-form-select>
                    </b-col>
                </b-row>

                <!-- TOTALES -->
                <b-row class="mb-4 p-3 bg-info text-white rounded">
                    <b-col md="4">
                        <p class="mb-0"><b>{{ labels.totalFuncional }}:</b> {{ formatoNumero(totalFuncional) }}</p>
                    </b-col>
                    <b-col md="4">
                        <p class="mb-0"><b>{{ labels.totalPromedio }}:</b> {{ formatoNumero(totalPromedio) }}</p>
                    </b-col>
                    <b-col md="4">
                        <p class="mb-0"><b>{{ labels.totalInterna }}:</b> {{ formatoNumero(totalInterna) }}</p>
                    </b-col>
                </b-row>

                <!-- BOTONES -->
                <b-row class="mt-3">
                    <b-col cols="12" class="text-center">
                        <b-button variant="primary" @click="grabeAjusteTopside()" :disabled="!v_cuenta || isVigenciaBloqueada"
                            class="px-4 mx-2 btn-lg">
                            <font-awesome-icon :icon="['fas', 'check-circle']" class="mr-1" />
                            {{ labels.save }}
                        </b-button>
                        <b-button variant="outline-secondary" @click="showDialogEdit = false" class="px-4 mx-2 btn-lg">
                            <font-awesome-icon :icon="['fas', 'times-circle']" class="mr-1" />
                            {{ labels.cancel }}
                        </b-button>
                    </b-col>
                </b-row>
            </b-container>

            <!-- ==================== SPINNER DE CARGA ==================== -->
            <div v-if="isLoading" class="text-center py-5">
                <b-spinner variant="primary" style="width: 3rem; height: 3rem;"></b-spinner>
                <h4 class="text-primary mt-3"><b><i>{{ labels.loading }}</i></b></h4>
            </div>

        </div>

        <!-- ==================== MODAL DETALLE ==================== -->
        <b-modal
            v-model="showDialogTopside"
            :title="f_titulo()"
            size="md"
            @hidden="showDialogTopside = false"
            header-class="modal-header-custom"
            footer-class="modal-footer-custom"
        >
            <b-row class="border-bottom">
                <b-col cols="3"><h5><b><i>{{ labels.cuenta }}</i></b></h5></b-col>
                <b-col cols="3"><h5>{{ formTopside?.cuenta }}</h5></b-col>
                <b-col cols="6"><h5>{{ formTopside?.descripcion }}</h5></b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col cols="4" class="border"><h5><b><i>{{ labels.funcional1 }}</i></b></h5></b-col>
                <b-col cols="4" class="border"><h5><b><i>{{ labels.promedio1 }}</i></b></h5></b-col>
                <b-col cols="4" class="border"><h5><b><i>{{ labels.interna1 }}</i></b></h5></b-col>
            </b-row>
            <b-row class="mb-2">
                <b-col cols="4" class="border"><h5>{{ formTopside?.ajuste_funcional1 }}</h5></b-col>
                <b-col cols="4" class="border"><h5>{{ formTopside?.ajuste_promedio1 }}</h5></b-col>
                <b-col cols="4" class="border"><h5>{{ formTopside?.ajuste_interna1 }}</h5></b-col>
            </b-row>
            <b-row>
                <b-col cols="4" class="border"><h5><b><i>{{ labels.funcional2 }}</i></b></h5></b-col>
                <b-col cols="4" class="border"><h5><b><i>{{ labels.promedio2 }}</i></b></h5></b-col>
                <b-col cols="4" class="border"><h5><b><i>{{ labels.interna2 }}</i></b></h5></b-col>
            </b-row>
            <b-row>
                <b-col cols="4" class="border"><h5>{{ formTopside?.ajuste_funcional2 }}</h5></b-col>
                <b-col cols="4" class="border"><h5>{{ formTopside?.ajuste_promedio2 }}</h5></b-col>
                <b-col cols="4" class="border"><h5>{{ formTopside?.ajuste_interna2 }}</h5></b-col>
            </b-row>

            <template #footer>
                <b-button variant="danger" @click="borraAjusteTopside(formTopside.secuencia)" :disabled="isVigenciaBloqueada">
                    <font-awesome-icon :icon="['fas', 'trash']" class="mr-1" />
                    {{ labels.delete }}
                </b-button>
                <b-button variant="secondary" @click="showDialogTopside = false">
                    {{ labels.close }}
                </b-button>
            </template>
        </b-modal>

    </div>
</template>

<script>
import Balance from "@/services/BalanceNiif"
import PlanConsolidacion from "@/services/PlanConsolidacion"
import Grupo from "@/services/Grupo"
import AjustesConsol from "@/services/AjustesConsol"
import CorporateDataTable from '../utils/CorporateDataTable.vue'

const ordenefechas = (arreglo = undefined) => [...arreglo].sort((a, b) => a - b)

export default {
    name: "BalanceNiif",
    components: {
        CorporateDataTable
    },
    data: () => ({
        // ==================== VARIABLES DE ESTADO ====================
        showDialogEdit: false,
        showDialogTopside: false,
        ajustesTopside: [],
        isLoading: false,
        error: false,
        isEmpty: true,
        fgl_consultar: false,

        // ==================== FILTROS ====================
        dialogReportes: '',
        dialogFechas: '',
        dialogCompanias: '',
        grupoId: 'all',
        reclasifica: false,
        idCompania: 1,
        vigencia: '',
        codigoReportePrincipal: null,

        // ==================== LISTAS ====================
        companias: [],
        companiasFiltradas: [],
        companiaUsuario: [],
        fechas: [],
        reportes: [],
        listGrupos: [],
        cuentas: [],
        niif: [],
        niifCuentas: [],
        moneda: 0,

        // ==================== FORMULARIO ====================
        formEdit: {
            cuenta: "",
            descripcion: '',
            niif: '',
            niif_desc: '',
            saldo_actual: 0,
            funcional1: 0,
            promedio1: 0,
            interna1: 0,
            funcional2: 0,
            promedio2: 0,
            interna2: 0,
            ajuste_actual: 0,
            ajuste_funcional1: '',
            ajuste_promedio1: '',
            ajuste_interna1: '',
            ajuste_funcional2: '',
            ajuste_promedio2: '',
            ajuste_interna2: '',
            niif1: '',
            porcentaje: 1,
            u_crea: 1,
            tipo_ajuste: 0,
            codReporte: ''
        },
        formTopside: {},

        // ==================== VARIABLES DE CUENTA ====================
        v_cuenta: '',
        v_id: null,
        v_niif: '',
        v_niif1: null,
        v_descripcion: '',
        v_ajuste_funcional1: 0,
        v_ajuste_promedio1: 0,
        v_ajuste_interna1: 0,
        v_ajuste_funcional2: 0,
        v_ajuste_promedio2: 0,
        v_ajuste_interna2: 0,
        vt_ajuste_funcional1: 0,
        vt_ajuste_promedio1: 0,
        vt_ajuste_interna1: 0,
        vt_ajuste_funcional2: 0,
        vt_ajuste_promedio2: 0,
        vt_ajuste_interna2: 0,

        // ==================== BÚSQUEDA ====================
        busquedaCuenta: '',
        sugerenciasCuentas: [],
        selectedRow: null,
        tituloConsolidado: "",
    }),
    computed: {
        isVigenciaBloqueada() {
            return this.dialogFechas ? !this.$store.getters.isPeriodoEnabled(this.dialogFechas) : false
        },
        labels() {
            const es = this.$store.state._language == "es-ES"
            return {
                title: this.reclasifica ? (es ? "Reclasificación" : "Reclassification") : (es ? "Ajustes Topside" : "Topside Adjustment"),
                selectReport: es ? "Seleccione Reporte" : "Select Report",
                selectValidity: es ? "Seleccione Vigencia" : "Select Validity",
                selectCompany: es ? "Seleccione Compañía" : "Select Company",
                allGroups: es ? "Todos los grupos" : "All groups",
                consultar: es ? "Consultar" : "Consult",
                secuencia: es ? "Secuencia" : "Sequence",
                cuenta: es ? "Cuenta" : "Account",
                descripcion: es ? "Descripción" : "Description",
                niif: es ? "NIIF" : "NIIF",
                niif1: es ? "Reclasificación" : "Reclassification",
                funcional1: es ? "Funcional" : "Functional",
                promedio1: es ? "Promedio" : "Average",
                interna1: es ? "Interna" : "Internal",
                funcional2: es ? "Funcional 2" : "Functional 2",
                promedio2: es ? "Promedio 2" : "Average 2",
                interna2: es ? "Interna 2" : "Internal 2",
                ajuste_funcional1: es ? "Ajuste Funcional" : "Functional Adjustment",
                ajuste_promedio1: es ? "Ajuste Promedio" : "Average Adjustment",
                ajuste_interna1: es ? "Ajuste Interna" : "Internal Adjustment",
                ajuste_funcional2: es ? "Ajuste Funcional 2" : "Functional Adjustment 2",
                ajuste_promedio2: es ? "Ajuste Promedio 2" : "Average Adjustment 2",
                ajuste_interna2: es ? "Ajuste Interna 2" : "Internal Adjustment 2",
                totalFuncional: es ? "Total Funcional" : "Total Functional",
                totalPromedio: es ? "Total Promedio" : "Total Average",
                totalInterna: es ? "Total Interna" : "Total Internal",
                searchAccount: es ? "Buscar Cuenta" : "Search Account",
                clear: es ? "Limpiar" : "Clear",
                save: es ? "Guardar" : "Save",
                cancel: es ? "Cancelar" : "Cancel",
                delete: es ? "Borrar" : "Delete",
                edit: es ? "Editar" : "Edit",
                close: es ? "Cerrar" : "Close",
                loading: es ? "Cargando información..." : "Loading information...",
                empty: es ? "No hay datos disponibles" : "No data available",
                selectCriteria: es ? "Seleccione los filtros y presione Consultar" : "Select filters and press Consult",
                noData: es ? "No hay datos disponibles" : "No data available",
                noMatches: es ? "No se encontraron resultados" : "No results found",
                searchPlaceholder: es ? "Buscar en la tabla..." : "Search in table...",
                rowsPerPage: es ? "Filas por página:" : "Rows per page:",
                noSort: es ? "Sin orden" : "No sorting",
                sortAsc: es ? "A-Z / Menor a mayor" : "A-Z / Low to high",
                sortDesc: es ? "Z-A / Mayor a menor" : "Z-A / High to low",
                columns: es ? "Columnas" : "Columns",
                showAllColumns: es ? "Mostrar todas" : "Show all",
                filters: es ? "Filtros" : "Filters",
                clearFilters: es ? "Limpiar filtros" : "Clear filters",
                code: es ? "Código" : "Code",
                exportExcel: es ? "Exportar Excel" : "Export Excel",
                generar: es ? "Generar" : "Generate",
            }
        },
        tableLabels() {
            return {
                noRow: this.labels.noData,
                noMatches: this.labels.noMatches,
                searchPlaceholder: this.labels.searchPlaceholder,
                rowsPerPage: this.labels.rowsPerPage,
                noSort: this.labels.noSort,
                sortAsc: this.labels.sortAsc,
                sortDesc: this.labels.sortDesc,
                columns: this.labels.columns,
                showAllColumns: this.labels.showAllColumns,
                filters: this.labels.filters,
                clearFilters: this.labels.clearFilters
            }
        },
        allFields() {
            const fields = [
                { key: 'secuencia', label: this.labels.secuencia, sortable: true, thClass: 'text-center', tdClass: 'text-center align-middle', minWidth: '60px' }
            ]
            if (!this.reclasifica) {
                fields.push({ key: 'compania', label: this.labels.selectCompany, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '150px' })
            }
            fields.push(
                { key: 'cuenta', label: this.labels.cuenta, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '120px' },
                { key: 'descripcion', label: this.labels.descripcion, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '180px' },
                { key: 'niif', label: this.labels.niif, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '120px' }
            )
            if (this.reclasifica) {
                fields.push({ key: 'niif1', label: this.labels.niif1, sortable: true, thClass: 'text-left', tdClass: 'text-left align-middle', minWidth: '120px' })
            }
            fields.push(
                { key: 'ajuste_funcional1', label: this.labels.funcional1, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle', minWidth: '100px' },
                { key: 'ajuste_promedio1', label: this.labels.promedio1, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle', minWidth: '100px' },
                { key: 'ajuste_interna1', label: this.labels.interna1, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle', minWidth: '100px' },
                { key: 'ajuste_funcional2', label: this.labels.funcional2, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle', minWidth: '100px' },
                { key: 'ajuste_promedio2', label: this.labels.promedio2, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle', minWidth: '100px' },
                { key: 'ajuste_interna2', label: this.labels.interna2, sortable: true, thClass: 'text-right', tdClass: 'text-right align-middle', minWidth: '100px' }
            )
            if (!this.reclasifica && this.fgl_consultar) {
                fields.push({ key: 'accionEditar', label: '', thClass: 'text-center', tdClass: 'text-center', sortable: false, minWidth: '50px' })
            }
            return fields
        },
        tableFilterDefs() {
            const es = this.$store.state._language == "es-ES"
            const defs = [
                { key: 'secuencia', type: 'text', placeholder: es ? 'Filtrar secuencia...' : 'Filter sequence...' },
                { key: 'cuenta', type: 'text', placeholder: es ? 'Filtrar cuenta...' : 'Filter account...' },
                { key: 'descripcion', type: 'text', placeholder: es ? 'Filtrar descripción...' : 'Filter description...' }
            ]
            if (!this.reclasifica) {
                defs.push({ key: 'compania', type: 'text', placeholder: es ? 'Filtrar compañía...' : 'Filter company...' })
            }
            return defs
        },
        mapaNiifCuenta() {
            const mapa = {}
            this.niifCuentas.forEach(c => mapa[c.cuenta] = c.niif)
            return mapa
        },
        mapaNiif() {
            const mapa = {}
            this.niif.forEach(n => mapa[n.niif] = n.nameNiif)
            return mapa
        },
        totalFuncional() {
            return parseFloat(this.v_ajuste_funcional1 || 0) + parseFloat(this.v_ajuste_funcional2 || 0)
        },
        totalPromedio() {
            return parseFloat(this.v_ajuste_promedio1 || 0) + parseFloat(this.v_ajuste_promedio2 || 0)
        },
        totalInterna() {
            return parseFloat(this.v_ajuste_interna1 || 0) + parseFloat(this.v_ajuste_interna2 || 0)
        },
        opcionesNiifSelect() {
            return this.niif.map(n => ({ value: n.niif, text: `${n.niif} - ${n.nameNiif}` }))
        }
    },
    methods: {
        // ==================== MÉTODOS DE FILTROS ====================
        onFiltroChange() {
            this.isEmpty = true
            this.fgl_consultar = false
            this.ajustesTopside = []
            this.showDialogEdit = false
            this.showDialogTopside = false
            this.selectedRow = null
        },

        filtraCompaniasPorGrupo(valor = null) {
            const grupoIdSeleccionado = valor !== null ? valor : this.grupoId
            this.onFiltroChange()
            this.dialogCompanias = null

            if (grupoIdSeleccionado === 'all' || !grupoIdSeleccionado) {
                this.companiasFiltradas = [...this.companias]
            } else {
                const grupoIdStr = grupoIdSeleccionado.toString()
                this.companiasFiltradas = this.companias.filter(compania => {
                    if (!compania?.id_grupos) return false
                    const gruposCompania = compania.id_grupos.split(',').map(id => id.trim())
                    return gruposCompania.includes(grupoIdStr)
                })
            }
        },

        seleccionaCompania() {
            this.onFiltroChange()
            let buscaMoneda = this.companias.filter((item) => item.id == this.dialogCompanias)
            if (buscaMoneda.length > 0) {
                this.moneda = buscaMoneda[0].acronimo
            }
        },

        // ==================== MÉTODOS DE BÚSQUEDA DE CUENTA ====================
        buscarCuenta() {
            const texto = this.busquedaCuenta.trim().toLowerCase()
            if (texto.length > 0) {
                this.sugerenciasCuentas = this.cuentas.filter(cuenta =>
                    cuenta.cuenta.toLowerCase().includes(texto) ||
                    cuenta.descripcion.toLowerCase().includes(texto)
                ).slice(0, 10)
            } else {
                this.sugerenciasCuentas = []
            }
        },

        limpiarBusqueda() {
            this.busquedaCuenta = ''
            this.sugerenciasCuentas = []
        },

        seleccionarCuenta(cuenta) {
            this.v_id = cuenta.id
            this.f_datos_cuenta(cuenta.id)
            this.busquedaCuenta = ''
            this.sugerenciasCuentas = []
        },

        f_datos_cuenta(p_id) {
            const cuenta = this.cuentas.find((cuenta) => cuenta.id === p_id)
            if (cuenta) {
                this.v_descripcion = cuenta.descripcion
                this.v_cuenta = cuenta.cuenta
                this.v_niif = this.f_niifCuenta1(cuenta.cuenta)
            }
        },

        // ==================== MÉTODOS DE CONSULTA ====================
        consultar() {
            this.fgl_consultar = false
            this.isLoading = true
            this.isEmpty = true

            if (!this.dialogFechas) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "Seleccione una vigencia" : "Select a validity")
                this.isLoading = false
                return
            }

            const v_tipo_ajuste = this.reclasifica ? 1 : 0
            this.idCompania = this.dialogCompanias
            this.vigencia = this.dialogFechas

            // CASO RECLASIFICACIÓN
            if (this.reclasifica) {
                if (!this.dialogCompanias || !this.dialogReportes) {
                    this.$toast.warning(this.$store.state._language == "es-ES" ? "Seleccione compañía y reporte" : "Select company and report")
                    this.isLoading = false
                    return
                }

                Promise.all([
                    PlanConsolidacion.getPlanConsPorCompVige(this.dialogFechas, this.dialogReportes),
                    PlanConsolidacion.getPlanConsPorCuentaVige(this.dialogCompanias, this.dialogFechas, this.dialogReportes),
                    Balance.getAjustesTopside(this.idCompania, this.vigencia, this.dialogReportes, v_tipo_ajuste)
                ]).then(([niifResult, cuentasResult, ajustesResult]) => {
                    this.niif = niifResult.data.planConsNiif || []
                    this.niifCuentas = cuentasResult.data.planConsCuentas || []
                    this.ajustesTopside = (ajustesResult.data.ajustesTopside || []).sort((a, b) => a.secuencia - b.secuencia)
                    this.f_calculeTopside()
                    this.fgl_consultar = true
                    this.isLoading = false
                    this.isEmpty = this.ajustesTopside.length === 0
                }).catch((error) => {
                    console.error(error)
                    this.error = true
                    this.isLoading = false
                    this.$toast.error(this.$store.state._language == "es-ES" ? "Error al consultar reclasificaciones" : "Error consulting reclassifications")
                })
                return
            }

            // CASO TOPSIDE
            if (!this.dialogCompanias && !this.grupoId) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "Seleccione una compañía o un grupo" : "Select a company or group")
                this.isLoading = false
                return
            }

            // Si hay compañía seleccionada
            if (this.dialogCompanias) {
                this.idCompania = this.dialogCompanias
                Promise.all([
                    PlanConsolidacion.getPlanConsPorCompVige(this.dialogFechas, this.codigoReportePrincipal),
                    PlanConsolidacion.getPlanConsPorCuentaVige(this.dialogCompanias, this.dialogFechas, this.codigoReportePrincipal),
                    Balance.getAjustesTopside(this.idCompania, this.vigencia, this.codigoReportePrincipal, v_tipo_ajuste)
                ]).then(([niifResult, cuentasResult, ajustesResult]) => {
                    this.niif = niifResult.data.planConsNiif || []
                    this.niifCuentas = cuentasResult.data.planConsCuentas || []
                    const nombreCompania = this.companias.find(c => c.id == this.dialogCompanias)?.nombre || ''
                    this.ajustesTopside = (ajustesResult.data.ajustesTopside || []).map(item => ({
                        ...item,
                        idCompania: this.dialogCompanias,
                        nombreCompania: nombreCompania
                    })).sort((a, b) => a.secuencia - b.secuencia)
                    this.f_calculeTopside()
                    this.fgl_consultar = true
                    this.isLoading = false
                    this.isEmpty = this.ajustesTopside.length === 0
                }).catch((error) => {
                    console.error(error)
                    this.error = true
                    this.isLoading = false
                    this.$toast.error(this.$store.state._language == "es-ES" ? "Error al consultar ajustes Topside" : "Error consulting Topside adjustments")
                })
                return
            }

            // Si hay grupo seleccionado
            if (this.grupoId) {
                let companiasAConsultar = []
                if (this.grupoId === "all") {
                    companiasAConsultar = this.companias.map(c => c.id)
                } else {
                    const grupoIdStr = this.grupoId.toString()
                    companiasAConsultar = this.companias.filter(compania => {
                        if (!compania?.id_grupos) return false
                        const gruposCompania = compania.id_grupos.split(',').map(id => id.trim())
                        return gruposCompania.includes(grupoIdStr)
                    }).map(c => c.id)
                }

                if (companiasAConsultar.length === 0) {
                    this.$toast.warning(this.$store.state._language == "es-ES" ? "No hay compañías en el grupo seleccionado" : "No companies in the selected group")
                    this.isLoading = false
                    this.isEmpty = true
                    return
                }

                Balance.getAjustesTopsideMultiples(companiasAConsultar, this.dialogFechas, this.codigoReportePrincipal, v_tipo_ajuste)
                    .then(result => {
                        this.ajustesTopside = (result.data.ajustesTopside || []).map(item => {
                            const compania = this.companias.find(c => c.id == item.idCompania)
                            return {
                                ...item,
                                nombreCompania: compania ? compania.nombre : ''
                            }
                        }).sort((a, b) => a.secuencia - b.secuencia)
                        this.f_calculeTopside()
                        this.fgl_consultar = true
                        this.isLoading = false
                        this.isEmpty = this.ajustesTopside.length === 0
                        if (companiasAConsultar.length > 0 && this.niif.length === 0) {
                            PlanConsolidacion.getPlanConsPorCompVige(this.dialogFechas, this.codigoReportePrincipal)
                                .then(niifResult => {
                                    this.niif = niifResult.data.planConsNiif || []
                                }).catch(() => {})
                        }
                    })
                    .catch(err => {
                        console.error(err)
                        this.isLoading = false
                        this.$toast.error(this.$store.state._language == "es-ES" ? "Error al consultar ajustes" : "Error consulting adjustments")
                        this.isEmpty = true
                    })
            }
        },

        // ==================== MÉTODOS DE CREACIÓN ====================
        textoBotonNuevo() {
            return this.reclasifica ?
                (this.$store.state._language == "es-ES" ? "Nueva Reclasificación" : "New Reclassification") :
                (this.$store.state._language == "es-ES" ? "Nuevo Topside" : "New Topside")
        },

        crear() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "La vigencia seleccionada está deshabilitada para crear o editar" : "The selected validity is disabled for creating or editing")
                return
            }

            if (!this.dialogCompanias) {
                this.$toast.warning(this.$store.state._language == "es-ES" ? "Seleccione una compañía primero" : "Select a company first")
                return
            }

            this.showDialogEdit = true
            this.v_cuenta = ''
            this.v_descripcion = ''
            this.v_id = null
            this.v_niif = ''
            this.v_niif1 = null
            this.v_ajuste_funcional1 = 0
            this.v_ajuste_promedio1 = 0
            this.v_ajuste_interna1 = 0
            this.v_ajuste_funcional2 = 0
            this.v_ajuste_promedio2 = 0
            this.v_ajuste_interna2 = 0
            this.busquedaCuenta = ''
            this.sugerenciasCuentas = []
            this.f_calculeTopside()

            if (!this.reclasifica) {
                this.v_ajuste_funcional1 = -this.vt_ajuste_funcional1
                this.v_ajuste_promedio1 = -this.vt_ajuste_promedio1
                this.v_ajuste_interna1 = -this.vt_ajuste_interna1
                this.v_ajuste_funcional2 = -this.vt_ajuste_funcional2
                this.v_ajuste_promedio2 = -this.vt_ajuste_promedio2
                this.v_ajuste_interna2 = -this.vt_ajuste_interna2
            }

            const codigoReporte = this.reclasifica ? this.dialogReportes : this.codigoReportePrincipal
            Balance.getCuentas(this.dialogCompanias, this.dialogFechas, codigoReporte)
                .then((result) => {
                    this.cuentas = result.data.cuentas || []
                }).catch(() => this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar cuentas" : "Error loading accounts"))
        },

        f_calculeTopside() {
            this.vt_ajuste_funcional1 = 0
            this.vt_ajuste_promedio1 = 0
            this.vt_ajuste_interna1 = 0
            this.vt_ajuste_funcional2 = 0
            this.vt_ajuste_promedio2 = 0
            this.vt_ajuste_interna2 = 0

            if (this.reclasifica) return

            this.ajustesTopside.forEach((cuenta) => {
                if (cuenta.id !== '') {
                    this.vt_ajuste_funcional1 += parseFloat(cuenta.ajuste_funcional1 || 0)
                    this.vt_ajuste_promedio1 += parseFloat(cuenta.ajuste_promedio1 || 0)
                    this.vt_ajuste_interna1 += parseFloat(cuenta.ajuste_interna1 || 0)
                    this.vt_ajuste_funcional2 += parseFloat(cuenta.ajuste_funcional2 || 0)
                    this.vt_ajuste_promedio2 += parseFloat(cuenta.ajuste_promedio2 || 0)
                    this.vt_ajuste_interna2 += parseFloat(cuenta.ajuste_interna2 || 0)
                }
            })
        },

        // ==================== MÉTODOS DE GUARDADO ====================
        grabeAjusteTopside() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "La vigencia seleccionada está deshabilitada para crear o editar" : "The selected validity is disabled for creating or editing")
                return
            }

            let v_error = false
            let v_mensaje = ''

            this.formEdit.cuenta = this.v_cuenta
            this.formEdit.descripcion = this.v_descripcion
            this.formEdit.ajuste_funcional1 = this.v_ajuste_funcional1
            this.formEdit.ajuste_promedio1 = this.v_ajuste_promedio1
            this.formEdit.ajuste_interna1 = this.v_ajuste_interna1
            this.formEdit.ajuste_funcional2 = this.v_ajuste_funcional2
            this.formEdit.ajuste_promedio2 = this.v_ajuste_promedio2
            this.formEdit.ajuste_interna2 = this.v_ajuste_interna2
            this.formEdit.codReporte = this.reclasifica ? this.dialogReportes : this.codigoReportePrincipal
            this.formEdit.niif = this.v_niif
            this.formEdit.tipo_ajuste = this.reclasifica ? 1 : 0

            if (this.formEdit.cuenta == '') {
                this.$toast.error(this.$store.state._language == "es-ES" ? "Falta Cuenta" : "Account is missing")
                return
            }

            if (this.reclasifica) {
                this.formEdit.niif1 = this.v_niif1
                if (!this.formEdit.niif1) {
                    v_mensaje = this.$store.state._language == "es-ES" ? "Falta NIIF (Reclasificado)" : "NIIF (Reclassified) is missing"
                    v_error = true
                } else if (this.formEdit.niif == this.formEdit.niif1) {
                    v_mensaje = this.$store.state._language == "es-ES" ? "NIIF y NIIF (Reclasificado) deben ser diferentes" : "NIIF and NIIF (Reclassified) must be different"
                    v_error = true
                }
            }

            if (v_error) {
                this.$toast.error(v_mensaje)
                return
            }

            this.showDialogEdit = false
            this.formEdit.u_crea = this.$store.state.userId

            Balance.grabeAjusteTopside(this.dialogCompanias, this.dialogFechas, this.formEdit)
                .then((result) => {
                    if (result.status == 200) {
                        this.$toast.success(this.$store.state._language == "es-ES" ? "Información Grabada" : "Information Saved")
                        this.consultar()
                    } else {
                        this.$toast.error(this.$store.state._language == "es-ES" ? "Problema al grabar" : "Problem saving")
                    }
                })
                .catch(() => this.error = true)
        },

        // ==================== MÉTODOS DE ELIMINACIÓN ====================
        borraAjusteTopside(secuencia) {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "La vigencia seleccionada está deshabilitada para eliminar" : "The selected validity is disabled for deleting")
                return
            }

            this.showDialogTopside = false
            const v_tipo_ajuste = this.reclasifica ? 1 : 0

            Balance.borraAjusteTopside(this.dialogCompanias, this.dialogFechas, secuencia, v_tipo_ajuste)
                .then((result) => {
                    if (result.status == 200) {
                        this.$toast.success(this.$store.state._language == "es-ES" ? "Información Borrada" : "Information Deleted")
                        this.consultar()
                    } else {
                        this.$toast.error(this.$store.state._language == "es-ES" ? "Problema al Borrar" : "Problem deleting")
                    }
                })
                .catch(() => this.error = true)
        },

        // ==================== MÉTODOS DE DETALLE ====================
        f_muestra(r_topside) {
            if (r_topside?.id === '') return
            this.selectedRow = r_topside.secuencia
            this.showDialogTopside = true
            this.formTopside = r_topside
            this.formTopside.niif_desc = this.f_niif(r_topside.niif)
            this.formTopside.niif1_desc = this.f_niif(r_topside.niif1)
        },

        onRowSelected(item) {
            if (item) {
                this.selectedRow = item.secuencia
            }
        },

        // ==================== MÉTODOS AUXILIARES ====================
        f_titulo() {
            const titulos = {
                ajustesTopside: { es: "Ajustes Topside", en: "TOPSIDE ADJUSTMENT" },
                reclasificaBalance: { es: "Reclasificación saldos de balances", en: "BALANCE RECLASSIFICATION" }
            }
            const ruta = this.$route.name
            const titulo = this.$store.state._language == "es-ES" ? titulos[ruta]?.es : titulos[ruta]?.en
            this.tituloConsolidado = titulo || "Ajustes Topside"
            return this.tituloConsolidado
        },

        f_niifCuenta(cuenta) {
            const niif = this.mapaNiifCuenta[cuenta]
            return this.mapaNiif[niif] || ""
        },

        f_niifCuenta1(cuenta) {
            let a_niif = this.niifCuentas.filter((item) => item.cuenta == cuenta)
            if (a_niif.length > 0) {
                return a_niif[0]?.niif
            }
            return ""
        },

        f_niif(niif) {
            let a_descripcion = this.niif.filter((item) => item.niif == niif)
            if (a_descripcion.length > 0) {
                return a_descripcion[0]?.nameNiif
            }
            return ""
        },

        obtenerNombreCompania(idCompania) {
            const compania = this.companias.find(c => c.id == idCompania)
            return compania ? compania.nombre : ''
        },

        formato(numero) {
            return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })?.format(numero)
        },

        formatoNumero(numero) {
            if (numero === null || numero === undefined) return "0.00"
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }).format(numero)
        },

        goBack() {
            this.$router.back()
        }
    },
    watch: {
        dialogFechas() {
            if (this.isVigenciaBloqueada) {
                this.$toast.error(this.$store.state._language == "es-ES" ? "La vigencia seleccionada está deshabilitada para crear o editar" : "The selected validity is disabled for creating or editing")
            }
        }
    },
    mounted() {
        this.isLoading = true
        this.reclasifica = this.$route.name == "reclasificaBalance"

        const promises = [
            Grupo.getAllGrupos(),
            Balance.leaCompaniaUsuario(),
            Balance.getCompanias(true),
            Balance.getFechas()
        ]

        if (this.reclasifica) {
            promises.push(Balance.getReportes())
        } else {
            promises.push(AjustesConsol.getReportePrincipal())
        }

        Promise.all(promises)
            .then(([gruposResult, companiaResult, companiasResult, fechasResult, extraResult]) => {
                this.listGrupos = gruposResult.data.grupos || []
                this.companiaUsuario = companiaResult.data.companiaUsuario || []
                this.companias = (companiasResult.data.companias || []).filter(element => {
                    const v_usuario = this.companiaUsuario.find(r_usuario =>
                        r_usuario.id_usuario == this.$store.state.userId && r_usuario.id_compania == element.id)
                    return this.$store.state.userRole == 1 || v_usuario
                })
                this.companiasFiltradas = [...this.companias]
                this.fechas = ordenefechas(fechasResult.data.fechas || [])

                if (this.reclasifica) {
                    this.reportes = extraResult.data.reportes || []
                } else {
                    if (!extraResult.data?.codigo) {
                        throw new Error('No se encontró reporte principal')
                    }
                    this.codigoReportePrincipal = extraResult.data.codigo
                }

                this.isLoading = false
                this.f_titulo()
            })
            .catch((error) => {
                console.error('Error en mounted:', error)
                this.error = true
                this.isLoading = false
                this.$toast.error(this.$store.state._language == "es-ES" ? "Error al cargar los datos iniciales" : "Error loading initial data")
            })
    }
}
</script>

<style src="../main.css"></style>

<style scoped>
/* ========== ESTILOS DE FILTROS PRINCIPALES ========== */
.niif-header .filters-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    margin-left: 0 !important;
}

.niif-header .filter-select {
    min-width: 200px;
    max-width: 280px;
    width: auto;
    height: 38px;
    font-size: 14px;
    border-radius: 7px;
}

.niif-header .filter-select option {
    white-space: normal;
    word-break: break-word;
    padding: 4px 8px;
}

/* ========== BOTÓN CONSULTAR ========== */
.niif-header .consult-btn {
    height: 38px;
    min-height: 38px;
    padding: 0 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 14px;
    background-color: #6c757d !important;
    color: white !important;
    border: 1px solid #6c757d !important;
    cursor: pointer;
    flex-shrink: 0;
}

.niif-header .consult-btn:hover:not(:disabled) {
    background-color: #5a6268 !important;
    border-color: #545b62 !important;
}

.niif-header .consult-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ========== BOTÓN DE ACCIÓN EN TABLA ========== */
.table-action-btn {
    border: none;
    border-radius: 4px;
    transition: all 0.2s;
}

.table-action-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* ========== BOTÓN DE TOOLBAR ========== */
.table-toolbar-add-btn {
    height: 31px;
    min-height: 31px;
    padding: 0 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    border-radius: 7px;
    font-size: 13px;
    background-color: #1f4f83 !important;
    color: white !important;
    border: 1px solid #1f4f83 !important;
}

.table-toolbar-add-btn:hover:not(:disabled) {
    background-color: #1a446f !important;
    border-color: #1a446f !important;
}

.table-toolbar-add-btn:disabled {
    opacity: 0.65;
    cursor: not-allowed;
}

/* ========== ESTILOS DE FLECHA Y TÍTULO ========== */
.page-back-btn {
    padding: 0;
    font-size: 1.5rem;
    line-height: 1;
}

.back-icon {
    font-size: 1.75rem !important;
    width: 1.75rem !important;
    height: 1.75rem !important;
}

.param-title {
    font-size: 2rem !important;
    font-weight: 500;
}

/* ========== ESTILOS DE FORMULARIO ========== */
.hover-bg:hover {
    background-color: #e9ecef !important;
    cursor: pointer;
}

.cursor-pointer {
    cursor: pointer;
}

/* ========== ESTILOS DE MODALES ========== */
.modal-header-custom {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
    padding: 12px 20px;
}

.modal-footer-custom {
    background-color: #f8f9fa;
    border-top: 1px solid #dee2e6;
    padding: 12px 20px;
}

/* ========== RESPONSIVE ========== */
@media (max-width: 1200px) {
    .niif-header .filter-select {
        min-width: 160px;
        max-width: 220px;
        height: 36px;
        font-size: 13px;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 992px) {
    .niif-header .filters-container {
        flex-wrap: wrap;
        width: 100%;
        margin-top: 8px;
        margin-left: 0 !important;
        justify-content: flex-start;
        gap: 6px;
    }

    .niif-header .filter-select {
        min-width: 150px;
        max-width: 200px;
        height: 36px;
        font-size: 13px;
        flex: 1 1 auto;
    }

    .niif-header .consult-btn {
        height: 36px;
        min-height: 36px;
        font-size: 13px;
        padding: 0 16px;
    }
}

@media (max-width: 576px) {
    .param-title {
        font-size: 1.5rem !important;
    }

    .niif-header .filter-select {
        min-width: 100%;
        max-width: 100%;
        height: 36px;
        font-size: 13px;
        flex: 0 0 100%;
    }

    .niif-header .filters-container {
        gap: 4px;
    }

    .niif-header .consult-btn {
        width: 100%;
        flex: 0 0 100%;
        height: 36px;
        margin-left: 0 !important;
        margin-top: 4px;
        font-size: 13px;
        padding: 0 16px;
    }
}
</style>